import { CommonModule } from '@angular/common';
import { CdkDrag, CdkDragDrop, CdkDragHandle, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, TemplateRef } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';

import { ShFacet, ShSelectedFacetValue } from '../facet/entities/facet.entity';
import { ShFacetBodyContext, ShFacetComponent } from '../facet/facet.component';
import { ShIconModule } from '../icon/icon.module';

@Component({
    standalone: true,
    selector: 'sh-facet-list',
    templateUrl: './facet-list.component.html',
    styleUrls: ['./facet-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, ShIconModule, ShFacetComponent, TranslateModule, CdkDropList, CdkDrag, CdkDragHandle]
})
export class ShFacetListComponent {
    private allFacets: ShFacet[] = [];
    private pinned: ReadonlySet<string> | null = null;

    @Input() set facets(facets: ShFacet[]) {
        this.allFacets = facets ?? [];
        this.splitFacets();
    }

    get facets(): ShFacet[] {
        return this.allFacets;
    }

    /**
     * Keys this list keeps at the top: drawn first, drawn open, and left out of the drag list, so no
     * reorder can displace them and they carry no handle. The Taken panels pin "Mijn taken" this way.
     */
    @Input() set pinnedKeys(keys: ReadonlySet<string> | null) {
        this.pinned = keys;
        this.splitFacets();
    }

    @Input() showMoreMsg = 'Show more';
    @Input() blockShowMore = false;
    @Input() collapsible = false;
    @Input() expandedKeys: ReadonlySet<string> | null = null;

    @Input() reorderable = false;

    /**
     * HFBD-6: bodies that replace a facet's value list, looked up by facet key. A facet with no entry here
     * renders its stock value list, so a list that passes nothing behaves exactly as before.
     */
    @Input() bodyTemplates: ReadonlyMap<string, TemplateRef<ShFacetBodyContext>> | null = null;

    @Output() facetSelected: EventEmitter<ShSelectedFacetValue> = new EventEmitter<ShSelectedFacetValue>();
    @Output() facetsChange: EventEmitter<ShFacet[]> = new EventEmitter<ShFacet[]>();
    @Output() loadMore: EventEmitter<ShFacet> = new EventEmitter<ShFacet>();
    @Output() facetExpandToggled: EventEmitter<string> = new EventEmitter<string>();
    @Output() facetsReordered: EventEmitter<string[]> = new EventEmitter<string[]>();

    protected readonly dragDots: number[] = [0, 1, 2, 3, 4, 5];

    protected pinnedFacets: ShFacet[] = [];
    protected draggableFacets: ShFacet[] = [];

    private splitFacets(): void {
        const pinned: ReadonlySet<string> | null = this.pinned;

        if (!pinned || pinned.size === 0) {
            this.pinnedFacets = [];
            this.draggableFacets = this.allFacets;
            return;
        }

        this.pinnedFacets = this.allFacets.filter(facet => pinned.has(facet.key));
        this.draggableFacets = this.allFacets.filter(facet => !pinned.has(facet.key));
    }

    public bodyTemplateFor(facet: ShFacet): TemplateRef<ShFacetBodyContext> | null {
        return this.bodyTemplates?.get(facet.key) ?? null;
    }

    public isExpanded(facet: ShFacet): boolean {
        return this.expandedKeys?.has(facet.key) ?? false;
    }

    public onExpandToggle(facet: ShFacet): void {
        this.facetExpandToggled.emit(facet.key);
    }

    public onValueSelected(selectedFacetValue: ShSelectedFacetValue): void {
        this.facetSelected.emit(selectedFacetValue);
    }

    public onSelectionChange(changedFacet: ShFacet): void {
        const newState: ShFacet[] = this.facets.map(facet => {
            if (this.isSameFacet(changedFacet, facet)) {
                return changedFacet;
            }
            return facet;
        });
        this.facetsChange.emit(newState);
    }

    private isSameFacet(facet1: ShFacet, facet2: ShFacet): boolean {
        return facet1.key === facet2.key;
    }

    public onLoadMore(facet: ShFacet): void {
        this.loadMore.emit(facet);
    }

    public onDrop(event: CdkDragDrop<ShFacet[]>): void {
        const keys: string[] = this.draggableFacets.map(facet => facet.key);
        moveItemInArray(keys, event.previousIndex, event.currentIndex);
        this.facetsReordered.emit([...this.pinnedFacets.map(facet => facet.key), ...keys]);
    }
}
